if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopRailMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailControls();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      bindThumbnailControls() {
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.handleThumbnailClick.bind(this, mediaToSwitch.dataset.target));
        });
      }

      handleThumbnailClick(mediaId) {
        const targetIsVisible = Boolean(this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`));
        if (this.selectResolvableVariantForMedia(mediaId) || !targetIsVisible) return;

        this.setActiveMedia(mediaId, false);
      }

      refreshThumbnails() {
        this.elements.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        if (!this.elements.thumbnails) return;
        if (!this.isDesktopRail()) this.elements.thumbnails.resetPages?.();
        this.bindThumbnailControls();
      }

      isDesktopRail() {
        return this.desktopRailMql.matches && this.dataset.desktopLayout === 'thumbnail_slider';
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.selectResolvableVariantForMedia(mediaId);
      }

      get sectionId() {
        return this.id.replace('MediaGallery-', '');
      }

      get variantSelects() {
        return document.getElementById(`variant-selects-${this.sectionId}`);
      }

      getVariantCandidatesForMedia(mediaId) {
        const prefix = `${this.sectionId}-`;
        if (!mediaId?.startsWith(prefix)) return [];

        try {
          const variants = JSON.parse(this.variantSelects?.querySelector('[data-product-variants]')?.textContent || '[]');
          return variants.filter(
            (variant) => String(variant?.featured_media?.id) === mediaId.slice(prefix.length)
          );
        } catch (error) {
          const message = 'Product image and option matching is temporarily unavailable. Please reload the page.';
          console.error(message, error);
          this.dataset.mediaParityState = 'invalid';
          if (this.elements.liveRegion) {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.textContent = message;
          }
          return [];
        }
      }

      getResolvableVariantForMedia(mediaId, variantSelects) {
        const candidates = this.getVariantCandidatesForMedia(mediaId);
        if (candidates.length === 1) return candidates[0];
        if (candidates.length < 2) return null;
        if (!candidates.every((candidate) => Array.isArray(candidate.options))) return null;

        const currentOptions = Array.from(
          variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown')
        ).map((group) => (group.querySelector('input:checked') || group.querySelector('select option[selected]'))?.value);
        if (currentOptions.length !== candidates[0]?.options?.length || currentOptions.some((value) => value == null)) {
          return null;
        }

        const resolved = candidates.filter((candidate) =>
          candidate.options.every((value, index) => {
            const mediaFixesValue = candidates.every((other) => other.options[index] === value);
            return mediaFixesValue || value === currentOptions[index];
          })
        );
        return resolved.length === 1 ? resolved[0] : null;
      }

      getVariantOptionControls(variantSelects, variant) {
        const groups = Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));
        if (!Array.isArray(variant?.options) || groups.length !== variant.options.length) return null;

        const controls = variant.options.map((value, index) => {
          const group = groups[index];
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
          if (radio) return { control: radio, isSelect: false };

          const select = group.querySelector('select');
          const option = Array.from(select?.options || []).find((entry) => entry.value === value);
          return option ? { control: select, option, isSelect: true } : null;
        });

        return controls.every(Boolean) ? controls : null;
      }

      controlsMatchVariant(controls) {
        return controls.every(({ control, option, isSelect }) =>
          isSelect ? control.value === option.value : control.checked
        );
      }

      selectResolvableVariantForMedia(mediaId) {
        const variantSelects = this.variantSelects;
        const variant = variantSelects && this.getResolvableVariantForMedia(mediaId, variantSelects);
        if (!variantSelects || !variant) return false;

        const controls = this.getVariantOptionControls(variantSelects, variant);
        if (!controls || this.controlsMatchVariant(controls)) return false;

        controls.forEach(({ control, option, isSelect }) => {
          if (!isSelect) {
            control.checked = true;
            return;
          }

          Array.from(control.options).forEach((entry) => {
            entry.toggleAttribute('selected', entry === option);
          });
          control.value = option.value;
        });

        const lastControl = controls.at(-1).control;
        lastControl.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) {
          return false;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
            if (!this.isDesktopRail()) this.elements.thumbnails.resetPages?.();
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return true;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        return true;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isDesktopRail()) {
          thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
